<template>
	<Card>
		<form @submit.prevent="submit">
			<!-- Basic settings -->
			<TwoColumn class="fieldset">
				<FieldInput
					name="name"
					label="Campaign name"
					info="Maximum 32 characters"
					required
					v-model="fields.name"
					:errors="errors.name"
				/>
				<FieldSelect
					:disabled="edit"
					name="type"
					label="Submission type"
					required
					:options="types"
					v-model="fields.type"
				/>
			</TwoColumn>
			<!-- Terms textarea -->
			<FieldTextarea
				name="terms"
				class="fieldset"
				label="Campaign terms"
				info="Shown at the top of the submission form"
				v-model="fields.terms"
			/>
			<!-- Competition mode settings -->
			<TwoColumn class="fieldset">
				<FieldSelect
					:disabled="edit"
					name="mode"
					label="Campaign mode"
					required
					:options="modes"
					v-model="fields.mode"
				/>
				<FieldInput
					v-show="isCompetitionMode"
					name="termsLink"
					label="T&Cs link"
					info="Must be secure (https)"
					:required="isCompetitionMode"
					v-model="fields.termsLink"
					:errors="errors.termsLink"
				/>
			</TwoColumn>
			<!-- Leaderboard type -->
			<div class="fieldset">
				<TwoColumn>
					<FieldSelect
						required
						name="leaderboardType"
						label="Main leaderboard type"
						v-model="fields.leaderboardType"
						:options="leaderboardTypes"
					/>
					<FieldSelect
						required
						name="wallLeaderboardType"
						label="Wall leaderboard type"
						v-model="fields.wallLeaderboardType"
						:options="leaderboardTypes"
					/>
				</TwoColumn>
			</div>
			<!-- Campaign state -->
			<div class="fieldset">
				<label>Campaign options</label>
				<TwoColumn>
					<FieldCheckbox
						name="allowSubmissions"
						v-model="fields.allowSubmissions"
						>Allow submissions</FieldCheckbox
					>
					<FieldCheckbox name="allowVoting" v-model="fields.allowVoting"
						>Allow voting</FieldCheckbox
					>
					<FieldCheckbox name="showLeaderboard" v-model="fields.showLeaderboard"
						>Show leaderboard</FieldCheckbox
					>
					<FieldCheckbox name="showGallery" v-model="fields.showGallery"
						>Show gallery</FieldCheckbox
					>
				</TwoColumn>
			</div>
			<!-- Dialogues -->
			<div class="fieldset dialogue">
				<label>Dialogue settings*</label>
				<DialogueForm v-model="fields.dialogue" />
			</div>
			<!-- Mark active -->
			<TwoColumn v-if="!archived" class="fieldset">
				<div>
					<FieldCheckbox
						name="active"
						label="Mark as active"
						v-model="fields.active"
						>Activate this campaign</FieldCheckbox
					>
					<Note class="note"
						>Marking this campaign as <strong>active</strong> will deactivate
						any currently active campaign.</Note
					>
				</div>
			</TwoColumn>
			<div>
				<SubmitButton
					class="btn btn--submit"
					:label="submitLabel"
					v-bind="submitButton"
				/>
				<input
					v-if="edit"
					type="button"
					class="btn btn--cancel"
					value="Cancel"
					@click="cancel"
				/>
			</div>
		</form>
	</Card>
</template>

<script>
import { enums } from 'protoswipe-shared'
import DialogueForm from '@/components/admin/DialogueForm'
import Note from '@/components/admin/Note'
import Card from '@/components/shared/Card'
import FieldInput from '@/components/shared/FieldInput'
import FieldTextarea from '@/components/shared/FieldTextarea'
import FieldSelect from '@/components/shared/FieldSelect'
import FieldCheckbox from '@/components/shared/FieldCheckbox'
import SubmitButton from '@/components/shared/SubmitButton'
import TwoColumn from '@/components/shared/TwoColumn'
import form from '@/mixins/form'

// Select field options
const { CampaignType, CampaignMode, LeaderboardType } = enums

const capitalise = str => str[0].toUpperCase() + str.substr(1)
const enumToSelect = e =>
	Object.values(e).map(value => ({
		label: capitalise(value),
		value
	}))

export default {
	name: 'CampaignForm',
	props: {
		edit: {
			type: Boolean,
			default: false
		},
		archived: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			types: enumToSelect(CampaignType),
			modes: enumToSelect(CampaignMode),
			leaderboardTypes: enumToSelect(LeaderboardType)
		}
	},
	computed: {
		isCompetitionMode() {
			return this.fields.mode.value === CampaignMode.competition
		},
		submitLabel() {
			return this.edit ? 'Save' : 'Create'
		}
	},
	mixins: [form.campaign],
	components: {
		DialogueForm,
		Note,
		Card,
		FieldInput,
		FieldTextarea,
		FieldSelect,
		FieldCheckbox,
		SubmitButton,
		TwoColumn
	}
}
</script>

<style lang="scss" scoped>
@import '~scss/components';
@import '~scss/mq';

.fieldset {
	margin-bottom: 2rem;

	> label {
		@extend %field-label;
		margin-bottom: 0.75rem;
	}
}
.checkboxes .field-wrap + .field-wrap {
	margin-top: 0.5rem;
}
.btn {
	width: 100%;

	&--cancel {
		@include button--large($black, transparent, $black, $grey-90);
	}

	& + & {
		margin: 1rem 0 0 0;
	}

	@include mq(md) {
		width: auto;
		& + & {
			margin: 0 0 0 1rem;
		}
	}
}
.note {
	margin: 0.75rem 0 0;
}
</style>
